import type { BeforeSendFn, CapturedNetworkRequest } from 'posthog-js';
import { environment } from '@/utils/env.utils';
import { isSensitiveDataKey, redactedValue } from '@/utils/sensitiveData';

const SENSITIVE_QUERY_PARAM_KEYS = new Set(['orderid', 'applicantid', 'sessionid']);

const SENSITIVE_BODY_KEYS = new Set(['code', 'authenticationurl', 'orderid']);

/** Hosts whose requests are dropped from replay entirely (Loqate address lookups carry the typed address). */
const DROPPED_REQUEST_HOSTS = ['addressy.com'];

const EVENT_URL_PROPERTY_KEYS = ['$current_url', '$initial_current_url', '$session_entry_url', '$referrer'];

const isSensitiveQueryParam = (key: string) =>
  SENSITIVE_QUERY_PARAM_KEYS.has(key.toLowerCase()) || isSensitiveDataKey(key);

const isSensitiveBodyKey = (key: string) =>
  SENSITIVE_BODY_KEYS.has(key.toLowerCase()) || isSensitiveDataKey(key);

const parseAbsoluteUrl = (url: string) => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

const isDroppedHost = (url: string) => {
  const hostname = parseAbsoluteUrl(url)?.hostname;
  return !!hostname && DROPPED_REQUEST_HOSTS.some(host => hostname === host || hostname.endsWith(`.${host}`));
};

/** Escape env values before interpolating into a RegExp (e.g. `apiRootSuffix`). */
const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const maskBodyValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(maskBodyValue);
  }

  if (typeof value !== 'object' || value === null) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      isSensitiveBodyKey(key) ? redactedValue : maskBodyValue(item),
    ]),
  );
};

/** Redacts sensitive field values in a JSON body. Non-JSON bodies are left unchanged. */
export const maskNetworkBody = (body: string | null | undefined) => {
  if (!body) {
    return body;
  }

  try {
    return JSON.stringify(maskBodyValue(JSON.parse(body)));
  } catch {
    return body;
  }
};

/**
 * Redacts sensitive path segments:
 * - FI: `{apiRootSuffix}/login/:sessionId` (only when `apiRootSuffix` is set)
 * - SE: `/se/login/:orderId`, `/bankid/:orderId/status`, `/bankid/:orderId/retry`
 */
const maskSensitivePathname = (pathname: string) => {
  let next = pathname;

  if (environment.apiRootSuffix) {
    const fiLoginSessionPath = new RegExp(`(${escapeRegExp(environment.apiRootSuffix)}/login/)[^/]+`, 'i');
    next = next.replace(fiLoginSessionPath, `$1${redactedValue}`);
  }

  next = next.replace(/(\/se\/login\/)[^/]+/i, `$1${redactedValue}`);
  next = next.replace(/(\/bankid\/)[^/]+(\/(?:status|retry))/i, `$1${redactedValue}$2`);

  return next;
};

/**
 * Redacts sensitive query-param values and BankID / FI login path segments.
 * Non-absolute values such as `$referrer: '$direct'` are returned unchanged.
 */
export const maskNetworkUrl = (url: string) => {
  const parsed = parseAbsoluteUrl(url);
  if (!parsed) {
    return url;
  }

  let changed = false;

  for (const key of [...parsed.searchParams.keys()]) {
    if (isSensitiveQueryParam(key)) {
      parsed.searchParams.set(key, redactedValue);
      changed = true;
    }
  }

  const maskedPathname = maskSensitivePathname(parsed.pathname);
  if (maskedPathname !== parsed.pathname) {
    parsed.pathname = maskedPathname;
    changed = true;
  }

  return changed ? parsed.href : url;
};

/** PostHog `session_recording.maskCapturedNetworkRequestFn`. Also used for replay page URLs. */
export const maskCapturedNetworkRequest = (request: CapturedNetworkRequest) => {
  if (isDroppedHost(request.name)) {
    return null;
  }

  return {
    ...request,
    name: maskNetworkUrl(request.name),
    requestBody: maskNetworkBody(request.requestBody),
    responseBody: maskNetworkBody(request.responseBody),
  };
};

const maskUrlProperties = (properties: Record<string, unknown>) => {
  const masked = { ...properties };

  for (const key of EVENT_URL_PROPERTY_KEYS) {
    const value = masked[key];
    if (typeof value === 'string') {
      masked[key] = maskNetworkUrl(value);
    }
  }

  return masked;
};

/**
 * PostHog `before_send`: scrubs analytics URL properties (`$current_url` etc.), which
 * replay masking does not cover. Not `mask_personal_data_properties`, because that also
 * masks advertising click IDs (gclid, fbclid, …) used for attribution.
 */
export const maskPostHogCaptureUrls: BeforeSendFn = event => {
  if (!event) {
    return event;
  }

  return {
    ...event,
    properties: maskUrlProperties(event.properties),
    ...(event.$set ? { $set: maskUrlProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: maskUrlProperties(event.$set_once) } : {}),
  };
};
